import classNames from 'classnames';
import { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import Icon from '@/components/atoms/icon/icon';
import Logo from '@/components/atoms/logo/logo';
import Button from '@/components/molecules/button/button';
import variables from '@/components/organisms/main-navigation/main-navigation.variables';
import MainNavigationSharedBrandsDropdown from '@/components/organisms/main-navigation/main-navigation-shared/main-navigation-shared-brands-dropdown/main-navigation-shared-brands-dropdown';
import Link from '@/components/utilities/link/link';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { usePageDetailsContext } from '@/libraries/contexts/page-details-context/page-details-context';
import { getLogoName } from '@/libraries/getters/get-logo-name';
import { usePageTransition } from '@/libraries/hooks/use-page-transition';
import useResizeObserver from '@/libraries/hooks/use-resize-observer';
import { MainNavigationMenuItem } from '../main-navigation';
import MainNavigationSmallDropdown from './main-navigation-small-dropdown/main-navigation-small-dropdown';
import MainNavigationSmallMenuItemList from './main-navigation-small-menu-item-list/main-navigation-small-menu-item-list';
import styles from './main-navigation-small.module.scss';

export interface MainNavigationSmallProps {
  /** The link to use for booking */
  bookingLink: Content.Properties.Link | null;
  /** The brand of the navigation */
  brand: Content.Properties.Brand;
  /** An array of sub brand items to switch context */
  brandNavigationItems: Content.Properties.BrandNavigationItem[];
  /** The class name to apply */
  className?: string;
  /** The link to use for content search */
  contentSearchPageLink: Content.Properties.Link | null;
  /** The link to use for favorites */
  favoritesPageLink: Content.Properties.Link | null;
  /** The link to use for the logo */
  homePageLink: Content.Properties.Link | null;
  /** Indicates whether the navigation is open */
  isMenuOpen: boolean;
  /** Indicates whether the sub brands dropdown is open */
  isSubBrandsMenuOpen?: boolean;
  /** Indicates if the navigation should be shown */
  isVisible?: boolean;
  /** An array of links to show */
  menuItems?: MainNavigationMenuItem[];
  /** An array of profile menu items */
  profileMenu: Content.Properties.ProfileMenu | null;
  /** An array of quick links to show */
  quickAccessItems?: MainNavigationMenuItem[];
  /** Changes the open state of the navigation */
  setIsMenuOpen: Dispatch<SetStateAction<boolean>>;
  /** Changes the state of the sub brands dropdown */
  setIsSubBrandsMenuOpen?: Dispatch<SetStateAction<boolean>>;
}

const SCROLL_UP_THRESHOLD = 16;

const MainNavigationSmall = ({
  bookingLink,
  brand,
  brandNavigationItems = [],
  className,
  contentSearchPageLink,
  favoritesPageLink,
  homePageLink,
  isMenuOpen = false,
  isSubBrandsMenuOpen = false,
  isVisible = false,
  menuItems = [],
  profileMenu,
  quickAccessItems = [],
  setIsMenuOpen,
  setIsSubBrandsMenuOpen,
}: MainNavigationSmallProps) => {
  const {
    hasInPageNavigation,
    isScrolled,
    setIsScrolled,
    setStickyTopContentHeight,
    shouldUpdateIsScrolled,
  } = usePageDetailsContext();

  const { t } = useI18n();
  const lastScrollYRef = useRef(0);
  const lastHiddenYRef = useRef(0);
  const lastHashRef = useRef('');
  const mainNavigationHeightRef = useRef(0);
  const componentRef = useRef<HTMLElement>(null);
  const shouldHandleScrollRef = useRef<boolean>(shouldUpdateIsScrolled);

  const mainNavigationHeight = useResizeObserver(componentRef);

  useEffect(() => {
    shouldHandleScrollRef.current = shouldUpdateIsScrolled;
  }, [shouldUpdateIsScrolled]);

  useEffect(() => {
    if (isScrolled && !hasInPageNavigation) {
      setStickyTopContentHeight(0);

      return;
    }

    mainNavigationHeightRef.current = mainNavigationHeight;

    setStickyTopContentHeight(
      mainNavigationHeight + variables.mainNavigationSmallMarginTop,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isScrolled, mainNavigationHeight]);

  const handleSubBrandsDropdown = () => {
    setIsSubBrandsMenuOpen?.(!isSubBrandsMenuOpen);
  };

  const handleKeyup = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsSubBrandsMenuOpen?.(false);
    }
  };

  const handleScroll = () => {
    if (!window) return;

    if (!shouldHandleScrollRef.current) {
      lastHiddenYRef.current = window.scrollY;
      lastScrollYRef.current = window.scrollY;

      return;
    }

    const currentHash = window.location.hash;
    const currentScrollY = window.scrollY;
    const scrollDifference = currentScrollY - lastScrollYRef.current;
    const topContentHeight =
      mainNavigationHeightRef.current + variables.mainNavigationSmallMarginTop;

    if (scrollDifference > 0 && currentScrollY > topContentHeight) {
      setIsScrolled(true);
      lastHiddenYRef.current = currentScrollY;
    } else if (
      scrollDifference < 0 &&
      lastHiddenYRef.current - currentScrollY >= SCROLL_UP_THRESHOLD &&
      currentHash === lastHashRef.current
    ) {
      setIsScrolled(false);
    }

    lastHashRef.current = currentHash;
    lastScrollYRef.current = currentScrollY;
  };

  usePageTransition({
    onEnter: () => {
      setIsMenuOpen(false);

      if (setIsSubBrandsMenuOpen) {
        setIsSubBrandsMenuOpen(false);
      }
    },
  });

  useEffect(() => {
    if (!window) return;

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('touchmove', handleScroll);
    window.addEventListener('wheel', handleScroll);
    window.addEventListener('keyup', handleKeyup);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('touchmove', handleScroll);
      window.removeEventListener('wheel', handleScroll);
      window.removeEventListener('keyup', handleKeyup);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      aria-hidden={!isVisible}
      className={classNames(styles.component, className, {
        [styles.isMenuOpen]: isMenuOpen,
        [styles.isScrolled]: isScrolled,
        [styles.isVisible]: isVisible,
      })}
    >
      <nav
        ref={componentRef}
        aria-label={t('items.common.words.navigation')}
        className={styles.navigation}
      >
        <div className={styles.header}>
          <div className={styles.buttons}>
            {brandNavigationItems.length > 0 && (
              <Button
                className={styles.button}
                icon={isSubBrandsMenuOpen ? 'ChevronUp' : 'ChevronDown'}
                onClick={handleSubBrandsDropdown}
                variant="ghost"
              />
            )}

            {homePageLink && (
              <div className={styles.logoWrapper}>
                <Link
                  aria-label={t('items.common.words.homepage')}
                  url={homePageLink.url}
                >
                  <Logo
                    className={styles.logo}
                    name={getLogoName({
                      brand,
                      type: 'Navigation',
                    })}
                    title={homePageLink.text}
                  />
                </Link>
              </div>
            )}

            {favoritesPageLink && (
              <Link
                aria-label={favoritesPageLink.text}
                className={classNames(styles.button, styles.isMyFavorites)}
                url={favoritesPageLink.url}
              >
                <Icon name="HeartFilled" size={24} />
              </Link>
            )}

            {bookingLink && (
              <Link
                aria-label={bookingLink.text}
                className={classNames(styles.button, styles.isMyBooking)}
                url={bookingLink.url}
              >
                <Icon name="User" size={24} />
              </Link>
            )}

            <button
              className={classNames(styles.button, styles.isMenu)}
              onClick={() => setIsMenuOpen(previousValue => !previousValue)}
            >
              <Icon name={isMenuOpen ? 'Close' : 'MenuBurger'} size={24} />
            </button>
          </div>
        </div>

        {quickAccessItems.length > 0 && (
          <div className={styles.quickAccessItemsWrapper}>
            <MainNavigationSmallMenuItemList
              className={styles.quickAccessItems}
              items={quickAccessItems}
            />
          </div>
        )}

        <MainNavigationSmallDropdown
          contentSearchPageLink={contentSearchPageLink}
          hasProfileMenuItems
          hasSearch
          isOpen={isMenuOpen}
          items={menuItems}
          onSearch={() => setIsMenuOpen(previousValue => !previousValue)}
          profileMenuItems={profileMenu?.items}
        />

        {brandNavigationItems.length > 0 && (
          <MainNavigationSharedBrandsDropdown
            brandNavigationItems={brandNavigationItems}
            isSubBrandsMenuOpen={isSubBrandsMenuOpen}
          />
        )}
      </nav>
    </header>
  );
};

MainNavigationSmall.displayName = 'MainNavigationSmall';

export default MainNavigationSmall;
